import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@skylabs-digital/backoffice-kit';
import { useFormData, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesApi } from '../../api/templates.api';
import type { Template, TemplateInput } from '../../schemas/template.schema';
import { templateInputSchema } from '../../schemas/template.schema';
import { ManagedByCacNotice } from '../shared/ManagedByCac';
import { TemplateEditor } from './TemplateEditor';

interface TemplateFormProps {
  template?: Template | null;
  isEditing: boolean;
  /** La plantilla es del CaC de la app: se muestra el aviso y no se guarda. */
  locked?: boolean;
}

/**
 * POST /templates accepts scope 'app' | 'system' only; 'tenant' is reachable
 * on an existing template but cannot be created. Offering it on the create form
 * produced a 400 from a control the UI presented as valid (noten-ohq).
 */
export function scopeOptionsFor(isEditing: boolean): readonly ('app' | 'tenant')[] {
  return isEditing ? (['app', 'tenant'] as const) : (['app'] as const);
}

const DEFAULT_VALUES: Partial<TemplateInput> = {
  eventType: '',
  channel: 'EMAIL',
  scope: 'app',
  tenantId: null,
  subject: '',
  fromName: '',
  // null, not '': the schema validates these as emails and '' is not one, so
  // an untouched form used to fail on fields nobody filled in.
  fromEmail: null,
  replyTo: null,
  bodyMjml: '',
  bodyText: '',
};

const DEFAULT_MJML = `<mjml>
  <mj-body>
    <mj-section>
      <mj-column>
        <mj-text>Hello {{name}},</mj-text>
        <mj-text>{{content}}</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

const CHANNEL_OPTIONS: Array<{
  value: TemplateInput['channel'];
  label: string;
  disabled?: boolean;
}> = [
  { value: 'EMAIL', label: 'EMAIL' },
  { value: 'SMS', label: 'SMS (coming soon)', disabled: true },
  { value: 'WHATSAPP', label: 'WHATSAPP (coming soon)', disabled: true },
  { value: 'PUSH', label: 'PUSH (coming soon)', disabled: true },
  { value: 'IN_APP', label: 'IN_APP (coming soon)', disabled: true },
];

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <span className="text-xs text-destructive">{message}</span>;
}

export function TemplateForm({ template, isEditing, locked = false }: TemplateFormProps) {
  const scopeOptions = scopeOptionsFor(isEditing);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const { values, errors, handleInputChange, handleChange, handleSubmit, reset, dirty } =
    useFormData(templateInputSchema, DEFAULT_VALUES);

  const { mutate: createTemplate, loading: creating } = templatesApi.useCreate();
  const { mutate: updateTemplate, loading: updating } = templatesApi.useUpdate();

  const saving = creating || updating;

  useEffect(() => {
    if (template && isEditing) {
      reset({
        eventType: template.eventType,
        channel: template.channel,
        scope: template.scope,
        tenantId: template.tenantId,
        subject: template.subject ?? '',
        fromName: template.fromName ?? '',
        fromEmail: template.fromEmail ?? '',
        replyTo: template.replyTo ?? '',
        bodyMjml: template.bodyMjml ?? null,
        bodyText: template.bodyText ?? '',
      });
    } else if (!isEditing) {
      reset({ ...DEFAULT_VALUES, bodyMjml: DEFAULT_MJML, bodyText: '' });
    }
  }, [template, isEditing, reset]);

  const onSave = handleSubmit(async (data) => {
    try {
      if (isEditing && template) {
        await updateTemplate(template.id, data);
      } else {
        await createTemplate(data);
      }
      showSnackbar({ message: 'Template saved successfully', variant: 'success' });
      navigate('/templates');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save template. Please try again.';
      showSnackbar({ message, variant: 'error' });
    }
  });

  const handleCancel = () => {
    if (dirty && !window.confirm('You have unsaved changes. Are you sure you want to leave?'))
      return;
    navigate('/templates');
  };

  return (
    <div className="space-y-6 pb-24">
      {locked ? <ManagedByCacNotice what="template" /> : null}
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>Event type, channel, and scope.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="tpl-eventType">Event type *</Label>
              <Input
                id="tpl-eventType"
                name="eventType"
                value={values.eventType ?? ''}
                onChange={handleInputChange}
                placeholder="e.g. welcome_email"
                disabled={saving}
                aria-invalid={!!errors.eventType}
              />
              <FieldError message={errors.eventType} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tpl-channel">Channel *</Label>
              <Select
                value={values.channel ?? 'EMAIL'}
                onValueChange={(v) => handleChange('channel', v as TemplateInput['channel'])}
              >
                <SelectTrigger id="tpl-channel" disabled={saving}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Scope</Label>
              <div className="flex gap-4 pt-2">
                {scopeOptions.map((scope) => (
                  <label key={scope} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      value={scope}
                      checked={values.scope === scope}
                      onChange={() => handleChange('scope', scope)}
                      disabled={saving}
                      className="accent-primary"
                    />
                    {scope === 'app' ? 'App-level' : 'Tenant-level'}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {values.channel === 'EMAIL' ? (
        <Card>
          <CardHeader>
            <CardTitle>Email headers</CardTitle>
            <CardDescription>
              Sender identity and subject. Use {'{{var}}'} for dynamic values.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="tpl-fromName">From name</Label>
                <Input
                  id="tpl-fromName"
                  name="fromName"
                  value={values.fromName ?? ''}
                  onChange={handleInputChange}
                  placeholder="Skylabs"
                  disabled={saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-fromEmail">From email</Label>
                <Input
                  id="tpl-fromEmail"
                  name="fromEmail"
                  type="email"
                  value={values.fromEmail ?? ''}
                  onChange={handleInputChange}
                  placeholder="noreply@yourapp.com"
                  disabled={saving}
                  aria-invalid={!!errors.fromEmail}
                />
                <FieldError message={errors.fromEmail} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-replyTo">Reply to</Label>
                <Input
                  id="tpl-replyTo"
                  name="replyTo"
                  type="email"
                  value={values.replyTo ?? ''}
                  onChange={handleInputChange}
                  placeholder="support@yourapp.com"
                  disabled={saving}
                  aria-invalid={!!errors.replyTo}
                />
                <FieldError message={errors.replyTo} />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label htmlFor="tpl-subject">Subject *</Label>
                <Input
                  id="tpl-subject"
                  name="subject"
                  value={values.subject ?? ''}
                  onChange={handleInputChange}
                  placeholder="Welcome to {{appName}}"
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Use {'{{variableName}}'} for dynamic content.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>MJML body</CardTitle>
          <CardDescription>Live MJML editor with preview.</CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateEditor
            value={values.bodyMjml ?? ''}
            onChange={(val) => handleChange('bodyMjml', val)}
            readOnly={saving}
          />
          <FieldError message={errors.bodyText} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Plain-text fallback</CardTitle>
          <CardDescription>
            Optional. If left empty, it's auto-generated from the HTML at send time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <textarea
            value={values.bodyText ?? ''}
            onChange={(e) => handleChange('bodyText', e.target.value)}
            readOnly={saving}
            placeholder="Hola {{customerName}}, tu pedido ha sido procesado."
            className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </CardContent>
      </Card>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-background/95 px-4 py-3 backdrop-blur md:-mx-8 md:px-8">
        <Button variant="outline" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={saving || locked}>
          {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create template'}
        </Button>
      </div>
    </div>
  );
}
